import React, {  useEffect, useMemo, useRef, useState } from "react";
import { FiCheck, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useClickOutside } from "../../../../components/hook/clickOutside";
import { FilterItem, FilterValue, formatFilterLabel, getCuisineDisplayLabel } from "../../../../components/types/types";
import { useSwipeDownToClose } from "../hookSearchPage";
import './hookFilterSearch.css'

const PRICE_MIN = 0;
const PRICE_MAX = 100;
const PRICE_STEP = 10;
const DEFAULT_PRICE: [number, number] = [0, 100];


const getTypeGroup = (value: FilterValue) => {
    if (typeof value !== "string") return "concept";
    if (value.startsWith("dish:") ||value.startsWith("regime:") ||value.startsWith("concept-unique:")) return "concept_unique";
    if ( value.startsWith("country:") ||value.startsWith("french-region:") ||value.startsWith("cultural-region:")) return "country";
    return "concept";
};
const isSameFilter = (a: FilterValue, b: FilterValue) => {
    if (Array.isArray(a) && Array.isArray(b)) {
        return a[0] === b[0] && a[1] === b[1];
    }

    return a === b;
};
const isInList = (list: FilterValue[], value: FilterValue) => {
    return list.some(elem => isSameFilter(elem, value));
};

interface HookFilterProps {
    searchValue:string,
    filter:FilterItem,
    listFilter:(string  |[number,number])[],
    addFilter:(filter:(string  |[number,number])) => void,
    applyFilterGroup:(groupValues: FilterValue[],selectedValues: FilterValue[]) => void,
    lockedFilterValues: Set<string>;
    filterResultCounts?: Map<string, number>;
}
const HookFilter:React.FC<HookFilterProps> = React.memo(({searchValue,filter,listFilter,addFilter,applyFilterGroup,lockedFilterValues,filterResultCounts}) => {
    const listSearch =  (filter.liste ?? []);
    const [show,setShow] = useState(true)
    const refContainer = useRef<HTMLDivElement | null>(null)

    const [isExpanded,setIsExpanded] = useState(false)
    useEffect(() => {
        if (!isExpanded) return;
        const listAllFilter = refContainer.current?.closest(
            ".listAllFilter"
        ) as HTMLElement | null;
        if (!listAllFilter) return;
        const previousOverflowY = listAllFilter.style.overflowY;
        listAllFilter.style.overflowY = "hidden";
        return () => {
            listAllFilter.style.overflowY = previousOverflowY;
        };
    }, [isExpanded]);
    const listRef = React.useRef<HTMLDivElement | null>(null);
    const [hasOverflow, setHasOverflow] = React.useState(false);

    const {dragHandlers} = useSwipeDownToClose({
        containerRef:refContainer,
        onClose: () => setIsExpanded(false),
    });
    const orderedFilterList = useMemo(() => {
        const liste = filter.liste ?? [];
        if (filter.value !== "Type de restaurant") {
            return liste;
        }
        return [...liste].sort(
            (a, b) =>
                (filterResultCounts?.get(b) ?? 0) -
                (filterResultCounts?.get(a) ?? 0)
        );
    }, [filter.liste, filter.value, filterResultCounts]);
    const groupedTypes = React.useMemo(() => {
        const groups = {
            country: [] as FilterValue[],
            concept: [] as FilterValue[],
            concept_unique: [] as FilterValue[],
        };

        orderedFilterList.forEach((elem) => {
            groups[getTypeGroup(elem)].push(elem);
        });

        return groups;
    }, [orderedFilterList]);

    const isTypeRestaurant = filter.value === "Type de restaurant";
    const isOrigineCuisine = filter.value === "Origine de la cuisine";

    useClickOutside(refContainer, isExpanded, () => {
        setIsExpanded(false);
    });

    const [tempSelect,setTempSelect] = useState<FilterValue[]>([])
    useEffect(() => {
        if (!isExpanded || !filter.liste) return;
        setTempSelect(
            filter.liste.filter(elem => isInList(listFilter, elem))
        );
    }, [isExpanded, filter.liste, listFilter]);
    const applyFilter = () => {
        if (!filter.liste) return;

        applyFilterGroup(filter.liste, tempSelect);
        setIsExpanded(false);
    };

    const fold = (value: unknown) => {
        if (value == null) return "";
        const s = typeof value === "string" ? value : String(value);
        const lower = s.toLowerCase();
        return /[^\x00-\x7F]/.test(lower)
            ? lower
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .replace(/œ/g, "oe")
                .replace(/æ/g, "ae")
                .replace(/ß/g, "ss")
            : lower;
    };
    const listFolded = React.useMemo(
        () => listSearch.map(raw => ({ raw, norm: fold(raw) })),
        [listSearch]
    );
    const needle = React.useMemo(
        () => fold((searchValue || "").trim()),
        [searchValue]
    );
    const shown = React.useMemo(() => {
        if (!needle) return [];
        return listFolded
            .filter(it => it.norm.includes(needle))
            .map(it => it.raw);
    }, [needle, listFolded]);

    useEffect(() => {
        const element = listRef.current;
        if (!element || searchValue || !show) {
            setHasOverflow(false);
            return;
        }
        let animationFrame = 0;
        const checkOverflow = () => {
            cancelAnimationFrame(animationFrame);

            animationFrame = requestAnimationFrame(() => {
                const previousMaxHeight = element.style.maxHeight;
                const previousOverflow = element.style.overflow;

                element.style.maxHeight = "60px";
                element.style.overflow = "hidden";

                const nextHasOverflow =
                    element.scrollHeight > element.clientHeight + 2;

                element.style.maxHeight = previousMaxHeight;
                element.style.overflow = previousOverflow;

                setHasOverflow(previous =>
                    previous === nextHasOverflow
                        ? previous
                        : nextHasOverflow
                );
            });
        };
        checkOverflow();

        const observer = new ResizeObserver(checkOverflow);
        observer.observe(element);

        return () => {
            cancelAnimationFrame(animationFrame);
            observer.disconnect();
        };
    }, [filter.liste, searchValue, show]);

    const clearCurrentFilter = () => {
        setTempSelect([])
    }
    const toggleTempSelect = (value: FilterValue) => {
        setTempSelect(prev => {
            const alreadySelected = isInList(prev, value);
            if (alreadySelected) return prev.filter(elem => !isSameFilter(elem, value));
            return [...prev, value];
        });
    };

    const numericFilters = useMemo(() => {
        let price: [number, number] | null = null;
        for (const filter of listFilter) {
            if (price === null &&Array.isArray(filter) &&filter.length === 2 &&filter.every(value => typeof value === "number")) {
                price = filter;
                continue;
            }
        }
        return {price};
    }, [listFilter]);
          

    const [minValue, setMinValue] = useState<number>(() => numericFilters.price?.[0] ?? PRICE_MIN);
    const [maxValue, setMaxValue] = useState<number>(() => (numericFilters.price?.[1] ?? PRICE_MAX));
    const [showCheck, setShowCheck] = useState(false);
    const checkTimerRef = useRef<number | null>(null);
    const applyTimerRef = useRef<number | null>(null);
    const scheduleApply = (fn: () => void, delay = 650) => {
        if (applyTimerRef.current) window.clearTimeout(applyTimerRef.current);
        applyTimerRef.current = window.setTimeout(() => {
            applyTimerRef.current = null;
            fn();
        }, delay);
    };
    useEffect(() => {
        return () => {
            if (applyTimerRef.current) window.clearTimeout(applyTimerRef.current);
        };
    }, []);

    useEffect(() => {
        const nextMin = numericFilters.price?.[0] ?? DEFAULT_PRICE[0];
        const nextMax = numericFilters.price?.[1] ?? DEFAULT_PRICE[1];
        setMinValue(previous =>previous === nextMin ? previous : nextMin);
        setMaxValue(previous =>previous === nextMax ? previous : nextMax);
    }, [numericFilters.price]);

    const triggerCheckAnimation = () => {
        setShowCheck(true);
        if (checkTimerRef.current) window.clearTimeout(checkTimerRef.current);
        checkTimerRef.current = window.setTimeout(() => {
            setShowCheck(false);
            checkTimerRef.current = null;
        }, 1500);
    };

    useEffect(() => {
        return () => {
            if (checkTimerRef.current) window.clearTimeout(checkTimerRef.current);
        };
    }, []);

    const middle = (minValue + maxValue) / 2;
    const priceWrapperRef = useRef<HTMLDivElement | null>(null);
    const addPrice = (nextMin: number, nextMax: number) => {
        const previousPrice = numericFilters.price;
        const groupValues: FilterValue[] = previousPrice? [previousPrice]: [];
        if (nextMin === PRICE_MIN && nextMax === PRICE_MAX) {
            applyFilterGroup(groupValues, []);
            return;
        }
        const newPrice: [number, number] = [nextMin, nextMax];
        applyFilterGroup(groupValues, [newPrice]);
    };
    const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = parseInt(e.target.value);
        if (value >= maxValue - PRICE_STEP) {
            value = maxValue - PRICE_STEP; 
        }
        setMinValue(value);
        const nextMin = value;
        const nextMax = Math.max(nextMin + PRICE_STEP, maxValue);
        scheduleApply(() => {
            addPrice(nextMin, nextMax);
            triggerCheckAnimation();
        })
    };
    const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = parseInt(e.target.value);
        if (value <= minValue + PRICE_STEP) {
            value = minValue + PRICE_STEP; 
        }
        setMaxValue(value);
        const nextMax = value;
        const nextMin = Math.min(nextMax - PRICE_STEP, minValue);

        scheduleApply(() => {
            addPrice(nextMin, nextMax);
            triggerCheckAnimation();
        })
    };
    const touchStartRef = useRef<{x: number; y: number} | null>(null);
    const priceTouchDirectionRef = useRef<"horizontal" | "vertical" | null>(null);
    const handlePriceTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        const touch = e.touches[0];
        touchStartRef.current = {x: touch.clientX,y: touch.clientY};
        priceTouchDirectionRef.current = null;
    };
    const handlePriceTouchMove = (e: React.TouchEvent<HTMLDivElement>,which: "min" | "max") => {
        const start = touchStartRef.current;
        if (!start) return;
        const touch = e.touches[0];

        const deltaX = touch.clientX - start.x;
        const deltaY = touch.clientY - start.y;
        if (priceTouchDirectionRef.current === null &&Math.abs(deltaX) < 6 &&Math.abs(deltaY) < 6) return;
        if (priceTouchDirectionRef.current === null) {
            priceTouchDirectionRef.current = Math.abs(deltaX) > Math.abs(deltaY) ? "horizontal" : "vertical";
        }
        if (priceTouchDirectionRef.current === "vertical") return;

        e.preventDefault();
        e.stopPropagation();
        updatePriceFromTouch(e, which);
    };
    const handlePriceTouchEnd = () => {
        touchStartRef.current = null;
        priceTouchDirectionRef.current = null;
    };
    const updatePriceFromTouch = (e: React.TouchEvent<HTMLDivElement>,which: 'min' | 'max') => {
        e.stopPropagation();

        const wrapper = priceWrapperRef.current;
        if (!wrapper) return;

        const touch = e.touches[0];
        const rect = wrapper.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const ratio = Math.max(0, Math.min(1, x / rect.width));

        const raw = PRICE_MIN + ratio * (PRICE_MAX - PRICE_MIN);
        const clamped = Math.max(PRICE_MIN, Math.min(PRICE_MAX, raw));
        let stepped = Math.round(clamped / PRICE_STEP) * PRICE_STEP;

        if (which === 'min') {
            if (stepped >= maxValue - PRICE_STEP) {
                stepped = maxValue - PRICE_STEP; 
            }
            const nextMin = Math.min(stepped, maxValue);
            const nextMax = maxValue;
            setMinValue(nextMin);
            scheduleApply(() => {
                addPrice(nextMin, nextMax);
                triggerCheckAnimation();
            }, 450);
        } else {
            if (stepped <= minValue + PRICE_STEP) {
                stepped = minValue + PRICE_STEP; 
            }
            const nextMin = minValue;
            const nextMax = Math.max(stepped, minValue);
            setMaxValue(nextMax);
            scheduleApply(() => {
                addPrice(nextMin, nextMax);
                triggerCheckAnimation();
            }, 450);
        }
    };
    const [openTypeGroups, setOpenTypeGroups] = useState({concept: true,country: true,concept_unique: true,});
    const toggleTypeGroup = (group: "concept" | "country" | "concept_unique") => {
        setOpenTypeGroups((prev) => ({...prev,[group]: !prev[group]}));
    };
    const [showAllConcept, setShowAllConcept] = useState(false);

    return <><div className={`elemFilter XsmallText ${searchValue ? ((shown.length>0) ? 'isSearch' : 'isNotSearch') : ''} ${!hasOverflow && 'isClosed'}`}>
         { searchValue ? <>
                {
                    shown.map((elem, index) => {
                        const formattedLabel = formatFilterLabel(elem);
                        return <div key={'elemSearch'+index} onClick={() => addFilter(elem)} className={`elem ${listFilter.includes(elem) ? 'isSelect' : ''}`}>{isOrigineCuisine ? getCuisineDisplayLabel(formattedLabel) : formattedLabel }</div>
                    }) 
                }
            </>
            :
            <>
                <span className="smallTextBold" onClick={() => setShow(!show)}>{filter.value}  {show ? <FiChevronUp className="Chevron" size={14}  /> : <FiChevronDown className="Chevron" size={14} />} </span>
                {   filter.value === "Prix par personne" ? <>
                        {show && <div className='filterList filterPrice' >
                            <div className="containerPrice XsmallText" onTouchStart={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}> 
                                <div className="value flexAllCenter" style={{marginLeft:`${minValue < 30 ? minValue+3 : minValue < 50 ? minValue+((50-minValue)/10) : minValue+((50-minValue)/10)+1}%`}}>{minValue+'€'}</div>
                                <div className="pointerValue" style={{marginLeft:`${minValue < 30 ? minValue+3 : minValue < 50 ? minValue+((50-minValue)/10) : minValue+((50-minValue)/10)+1}%`}}></div>
                                <div className="value flexAllCenter" style={{marginLeft:`${maxValue < 40 ?  maxValue + 2 : maxValue < 50 ?  maxValue + 1 : maxValue <= 90 ? maxValue : maxValue-2}%`}}>{maxValue === 100 ? '100€+' : maxValue+'€'}</div>
                                <div className="pointerValue" style={{marginLeft:`${maxValue < 40 ?  maxValue + 2 : maxValue < 50 ?  maxValue + 1 : maxValue <= 90 ? maxValue : maxValue-2}%`}}></div>                            


                                <div className="patchClose" onTouchStart={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()}></div>
                                <div className="range-wrapper" ref={priceWrapperRef}>
                                    <div className="price-touch-zone " style={{ left: 0, width: `${middle}%` }} onTouchStart={handlePriceTouchStart} onTouchMove={(e) => handlePriceTouchMove(e, "min")} onTouchEnd={handlePriceTouchEnd} />
                                    <div className="price-touch-zone " style={{ left: `${middle}%`, width: `${100 - middle}%` }}  onTouchStart={handlePriceTouchStart} onTouchMove={(e) => handlePriceTouchMove(e, "max")} onTouchEnd={handlePriceTouchEnd} />
                                    <input type="range" aria-label="Prix minimum en euros" id="minRange" min="0" max="100" step={PRICE_STEP} value={minValue} onChange={(e) => handleMinChange(e)}/>
                                    <input type="range" aria-label="Prix maximum en euros" id="maxRange" min="0" max="100" step={PRICE_STEP} value={maxValue} onChange={(e) => handleMaxChange(e)}/>
                                </div>
                                
                                <div className="slider-track" style={{left: `${Math.min(minValue, 88)}%`,width: `${Math.min(99, Math.max(11, maxValue - minValue))}%`}}></div>
                                {showCheck && <span className="checkAnimation"><FiCheck size={20} color="black" /></span>}
                                <div className="indicationSlide"><span>0 €</span><span>100 € +</span></div>
                            </div> 
                        </div>}
                    </>
                    :
                    show && orderedFilterList &&  <div ref={listRef} className='filterList' >
                        {orderedFilterList.map((elem,index) => {
                        const isLocked = typeof elem === "string" && lockedFilterValues.has(elem);
                        return <div key={'elemSearch'+index} onClick={() => {if(!isLocked){addFilter(elem)}}} className={`elem ${listFilter.includes(elem) ? 'isSelect' : ''} ${isLocked ? 'isLocked' : ''}`}>{isOrigineCuisine ? getCuisineDisplayLabel(formatFilterLabel(elem)) : formatFilterLabel(elem)}</div>
                    })}</div>
                }
                {hasOverflow  && show && (
                    <p className="seeMoreType XsmallText" onClick={() => setIsExpanded(true)}>
                        Tout afficher
                    </p>
                )}
            </>
         }
    </div>
    {isExpanded && <>
    <div className="maskContainerShowAllFilter"></div>
    <div className={`containerShowAllFilter smallText primaryBack ${(filter.value === 'Type de restaurant' || filter.value === 'Origine de la cuisine') ? 'isLarge' : ''}`} ref={refContainer}>
        <div className="topPart" {...dragHandlers}>
            <span className="midTextBold">{filter.value}</span>
            <FiChevronUp className="close pointer" size={23} onClick={() => setIsExpanded(false)} />
            
            <div className="shadowTop"></div>
          </div>
          <div className="separationTop"><div className="shadowTop"></div></div>
          <div className="contentPart">
            {isTypeRestaurant ? (
                <>
                    {groupedTypes.concept.length > 0 && (
                            <>
                            <div className="smallTextBold filterTypeGroupTitle" onClick={() => toggleTypeGroup("concept")}>Concept {!openTypeGroups.concept ? <FiChevronDown className="icon" size={18} /> :  <FiChevronUp className="icon" size={18} />}   </div>
                                {openTypeGroups.concept && (groupedTypes.concept.length >= 8 && !showAllConcept ? groupedTypes.concept.slice(0, 6) : groupedTypes.concept).map((elem, index) => {
                                    const isLocked = typeof elem === "string" && lockedFilterValues.has(elem);
                                    return <div key={"elemSearchConcept" + index} onClick={() => {if(!isLocked){toggleTempSelect(elem)}}} className={`elem ${isInList(tempSelect, elem) ? "isSelect" : ""} ${isLocked ? 'isLocked' : ''}`}>
                                        {formatFilterLabel(elem)}
                                    </div>
                                })}
                                {groupedTypes.concept.length >= 8 && (
                                    <div className="wrapperSeeMoreConcept"><div className="seeMoreConcept XsmallText" onClick={() => setShowAllConcept(prev => !prev)}>
                                        {showAllConcept ? "Afficher moins" : "Afficher plus"}
                                    </div>
                                    </div>
                                )}
                            </>
                    )}
                    {(groupedTypes.concept.length > 0 && (groupedTypes.country.length > 0 || groupedTypes.concept_unique.length > 0)) && <div className="sepfilterTypeGroup"></div>}
                    {groupedTypes.country.length > 0 && (
                        <>
                            <div className="smallTextBold filterTypeGroupTitle" onClick={() => toggleTypeGroup("country")}>Pays et régions {!openTypeGroups.country ? <FiChevronDown className="icon" size={18} /> :  <FiChevronUp className="icon" size={18} />}</div>
                            {openTypeGroups.country && groupedTypes.country.map((elem, index) => {
                                const isLocked = typeof elem === "string" && lockedFilterValues.has(elem);
                                return <div key={"elemSearchCountry" + index} onClick={() => {if(!isLocked){toggleTempSelect(elem)}}} className={`elem ${isInList(tempSelect, elem) ? "isSelect" : ""} ${isLocked ? 'isLocked' : ''}`}>
                                    {formatFilterLabel(elem)}
                                </div>
                            })}
                        </>
                    )}
                    {groupedTypes.country.length > 0 && groupedTypes.concept_unique.length > 0 && <div className="sepfilterTypeGroup"></div>}
                    {groupedTypes.concept_unique.length > 0 && (
                        <>
                            <div className="smallTextBold filterTypeGroupTitle" style={{marginTop:0}} onClick={() => toggleTypeGroup("concept_unique")}>Concept unique / rare {!openTypeGroups.concept_unique ? <FiChevronDown className="icon" size={18} /> :  <FiChevronUp className="icon" size={18} />}</div>
                            {openTypeGroups.concept_unique && groupedTypes.concept_unique.map((elem, index) => {
                                const isLocked = typeof elem === "string" && lockedFilterValues.has(elem);
                                return <div key={"elemSearchUnique" + index} onClick={() => {if(!isLocked){toggleTempSelect(elem)}}} className={`elem ${isInList(tempSelect, elem) ? "isSelect" : ""} ${isLocked ? 'isLocked' : ''}`}>
                                    {formatFilterLabel(elem)}
                                </div>
                            })}
                        </>
                    )}
                </>
            ) : (
                (isOrigineCuisine
                    ? [...(filter.liste ?? [])].sort((a, b) =>
                        getCuisineDisplayLabel(formatFilterLabel(a)).localeCompare(getCuisineDisplayLabel(formatFilterLabel(b)),"fr",{ sensitivity: "base" })
                    )
                    : filter.liste ?? []
                ).map((elem, index) => {
                    const isLocked = typeof elem === "string" && lockedFilterValues.has(elem);
                    return <div key={"elemSearch" + index} onClick={() => {if(!isLocked){toggleTempSelect(elem)}}} className={`elem ${isInList(tempSelect, elem) ? "isSelect" : ""} ${isLocked ? 'isLocked' : ''}`}>
                        {isOrigineCuisine ? getCuisineDisplayLabel(formatFilterLabel(elem)) : formatFilterLabel(elem)}
                    </div>
                })
            )}
          </div>
          <div className="bottomPart">
            <div className="cleanButton" onClick={clearCurrentFilter}>Tout effacer</div>
            <div className='midGoldRoundButton'  onClick={applyFilter}>Appliquer</div>
          </div>
    </div>
    </>}
    </>
})

export default HookFilter